import { PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Form, Input, Modal, Popconfirm, Table, Tag, Typography, message } from 'antd'
import { useEffect, useState } from 'react'
import { addSiteDomain, deleteSiteDomain, listSiteDomains, updateSiteDomain } from '../api'
import type { SaasApiError } from '../api'
import type { DomainBinding } from '../types'
import { useWorkspaceState } from './WorkspaceContext'
import '../ui/tenant-items.css'

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending verification',
  verification_required: 'Verification required',
  verified: 'Verified',
  ssl_pending: 'SSL pending',
  active: 'Active',
  error: 'Error',
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'orange',
  verification_required: 'orange',
  verified: 'blue',
  ssl_pending: 'purple',
  active: 'green',
  error: 'red',
}

// Spec §27/§28 — B's core first real interaction: [Add Domain] -> real
// DomainBinding via API -> honestly shows "Pending verification", never
// a faked "Active" (spec §12).
export default function WorkspaceDomainsPage() {
  const { primarySite, entitlements, sites } = useWorkspaceState()
  const [domains, setDomains] = useState<DomainBinding[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [form] = Form.useForm()

  const load = () => {
    if (!primarySite) return
    setError(null)
    listSiteDomains(primarySite.id)
      .then((r) => setDomains(r.data))
      .catch((err) => setError(err.message))
  }

  useEffect(load, [primarySite])

  if (sites.length === 0 || !primarySite) {
    return <Empty description="No sites in this workspace yet" style={{ marginTop: 80 }} />
  }

  const customDomainEnabled = Boolean(entitlements['feature.custom_domain'])

  const onAdd = async () => {
    const values = await form.validateFields()
    try {
      await addSiteDomain(primarySite.id, values.hostname.trim())
      message.success('Domain added — Pending verification')
      setAddOpen(false)
      form.resetFields()
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const setPrimary = async (domain: DomainBinding) => {
    try {
      await updateSiteDomain(primarySite.id, domain.id, { is_primary: true })
      message.success(`${domain.hostname} set as primary`)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const requestVerification = async (domain: DomainBinding) => {
    try {
      await updateSiteDomain(primarySite.id, domain.id, { status: 'verification_required' })
      message.success('Verification requested')
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const onDelete = async (domain: DomainBinding) => {
    try {
      await deleteSiteDomain(primarySite.id, domain.id)
      message.success(`${domain.hostname} removed`)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Domains
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!customDomainEnabled}
          onClick={() => setAddOpen(true)}
        >
          Add Domain
        </Button>
      </div>
      {!customDomainEnabled && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Custom Domain is not enabled for this workspace. Contact your Vistoera account manager."
        />
      )}
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Table
        className="tc-data-table"
        rowKey="id"
        loading={domains === null}
        dataSource={domains ?? []}
        sticky={{ offsetHeader: 64 }}
        columns={[
          { title: 'Hostname', dataIndex: 'hostname', align: 'left' as const },
          {
            title: 'Status',
            dataIndex: 'status',
            render: (s: string) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{STATUS_LABEL[s] ?? s}</Tag>,
          },
          {
            title: 'Primary',
            dataIndex: 'is_primary',
            render: (v: boolean) => (v ? <Tag color="gold">Primary</Tag> : <Typography.Text type="secondary">—</Typography.Text>),
          },
          {
            title: 'Actions',
            render: (_: unknown, row: DomainBinding) => (
              <span style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {!row.is_primary && (
                  <Button size="small" onClick={() => setPrimary(row)}>
                    Set Primary
                  </Button>
                )}
                {row.status === 'pending' && (
                  <Button size="small" onClick={() => requestVerification(row)}>
                    Request Verification
                  </Button>
                )}
                <Popconfirm
                  title={`Remove ${row.hostname}?`}
                  onConfirm={() => onDelete(row)}
                  okText="Confirm Delete"
                  cancelText="Cancel"
                >
                  <Button size="small" danger>
                    Delete
                  </Button>
                </Popconfirm>
              </span>
            ),
          },
        ]}
      />
      <Modal
        title="Add Domain"
        open={addOpen}
        onOk={onAdd}
        onCancel={() => setAddOpen(false)}
        okText="Add"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="hostname"
            label="Hostname"
            rules={[{ required: true, message: 'Hostname is required' }]}
            extra="e.g. liangcollection.com or www.liangcollection.com — no scheme, path, port, or wildcard."
          >
            <Input placeholder="liangcollection.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
